/**
 * Sessions — server state.
 *
 * Read-only, and cached against the activity rather than the session: every
 * screen that needs a session reached it through an activity, and asking for one
 * session on its own is not a thing any of them do.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  confirmSession,
  enrolInSession,
  listActivitySessions,
  listMySessions,
  listOpenSessions,
  fetchSessions,
  getSession,
  getSessionOverview,
  listRecentVolunteerSessions,
  listSessionAssignments,
  overrideSessionStatus,
  deleteSession,
  setSessionCapacity,
  setSessionRepresentative,
  updateSession,
  createSession,
} from '../services/api/sessions';
import type { AssignmentDetail, CreateSessionBody } from '@parinaam-vms/shared';
import type {
  ConfirmResult,
  Session,
  SessionListQuery,
  SessionOverview,
  RecentSession,
  CreatedSession,
} from '../services/api/sessions';

export const sessionKeys = {
  all: ['sessions'] as const,
  forActivity: (activityId: string) => ['sessions', 'activity', activityId] as const,
  mine: ['sessions', 'mine'] as const,
  open: ['sessions', 'open'] as const,
  one: (sessionId: string) => ['sessions', 'one', sessionId] as const,
  list: (params: SessionListQuery) => ['sessions', 'list', params] as const,
  overview: (sessionId: string) => ['sessions', 'overview', sessionId] as const,
  roster: (sessionId: string) => ['sessions', 'roster', sessionId] as const,
  recentForVolunteer: (volunteerId: string) =>
    ['sessions', 'recent', volunteerId] as const,
};

export function useActivitySessions(activityId: string) {
  return useQuery<Session[]>({
    queryKey: sessionKeys.forActivity(activityId),
    queryFn: () => listActivitySessions(activityId),
    enabled: Boolean(activityId),
  });
}

/**
 * Sessions across activities, for a picker.
 *
 * `useActivitySessions` above answers "what is under this activity"; this one
 * answers "which sessions are there", optionally narrowed. A screen offering a
 * session filter before an activity has been chosen needs the second question.
 */
export function useSessionList(params: SessionListQuery) {
  return useQuery({
    queryKey: sessionKeys.list(params),
    queryFn: () => fetchSessions(params),
  });
}

/**
 * One session's briefing, for a volunteer deciding whether to sign up.
 *
 * Keyed apart from `one` even though both are "a session by id": they come
 * from different endpoints with different shapes, and sharing a key would mean
 * whichever screen loaded first decided what the other one found in the cache.
 */
export function useSessionOverview(sessionId: string | undefined) {
  return useQuery<SessionOverview>({
    queryKey: sessionKeys.overview(sessionId ?? ''),
    queryFn: () => getSessionOverview(sessionId as string),
    enabled: Boolean(sessionId),
  });
}

/** The signed-in volunteer's own sessions. */
export function useMySessions() {
  return useQuery<Session[]>({
    queryKey: sessionKeys.mine,
    queryFn: listMySessions,
  });
}

/**
 * Confirm a session.
 *
 * Invalidates every session key rather than only the volunteer's own list: the
 * same session appears on the staff activity screen, and a confirmation that
 * updated one view and not the other would leave two screens disagreeing about
 * whether the work is done.
 */
export function useConfirmSession() {
  const queryClient = useQueryClient();

  return useMutation<ConfirmResult, Error, string>({
    mutationFn: (sessionId: string) => confirmSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/**
 * An admin setting a session's status by hand.
 *
 * Invalidates every session query rather than patching the one row: an
 * override can clear the confirmations, which changes `awaiting` and the
 * counts beside it, and a hand-patched cache would show a status that no
 * longer agrees with the reason printed under it.
 */
export function useOverrideSession() {
  const queryClient = useQueryClient();

  return useMutation<
    Session,
    Error,
    { sessionId: string; status: Session['status']; reason: string }
  >({
    mutationFn: ({ sessionId, status, reason }) =>
      overrideSessionStatus(sessionId, status, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** One session, for the screen that is about it. */
export function useSession(sessionId: string) {
  return useQuery<Session>({
    queryKey: sessionKeys.one(sessionId),
    queryFn: () => getSession(sessionId),
  });
}

/** Who is expected at one session. */
export function useSessionRoster(sessionId: string) {
  return useQuery<AssignmentDetail[]>({
    queryKey: sessionKeys.roster(sessionId),
    queryFn: () => listSessionAssignments(sessionId),
  });
}

/** Name the Parinaam team member responsible, or clear the name. */
export function useSetSessionRepresentative() {
  const queryClient = useQueryClient();

  return useMutation<Session, Error, { sessionId: string; userId: string | null }>({
    mutationFn: ({ sessionId, userId }) => setSessionRepresentative(sessionId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/**
 * Change how many volunteers a session needs.
 *
 * No toast of its own: the activity edit form saves this alongside the
 * activity's own fields and reports the whole save once, so a form touching
 * three sessions does not stack three notifications.
 */
export function useSetSessionCapacity() {
  const queryClient = useQueryClient();

  return useMutation<Session, Error, { sessionId: string; volunteersNeeded: number | null }>({
    mutationFn: ({ sessionId, volunteersNeeded }) => setSessionCapacity(sessionId, volunteersNeeded),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** Correct a session. The caller reports the outcome, as the capacity hook does. */
export function useUpdateSession() {
  const queryClient = useQueryClient();

  return useMutation<Session, Error, { sessionId: string; changes: Record<string, unknown> }>({
    mutationFn: ({ sessionId, changes }) => updateSession(sessionId, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** Remove one. Also invalidates activities, which list their sessions. */
export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation<{ deleted: boolean }, Error, { sessionId: string; reason: string }>({
    mutationFn: ({ sessionId, reason }) => deleteSession(sessionId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/** What this volunteer could sign up for. */
export function useOpenSessions() {
  return useQuery<Session[]>({
    queryKey: sessionKeys.open,
    queryFn: listOpenSessions,
  });
}

/**
 * Sign up for a session.
 *
 * Invalidates every session key: the row leaves "Find activities" and appears
 * under "My volunteering" at the same moment, and updating one without the
 * other would show the same place in two states.
 */
export function useEnrolInSession() {
  const queryClient = useQueryClient();

  return useMutation<Session, Error, string>({
    mutationFn: (sessionId: string) => enrolInSession(sessionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}

/**
 * Where a volunteer has been lately, for the form that records attendance
 * starting from the person.
 *
 * Invalidated by `useCreateAttendance` along with everything else attendance
 * touches, so recording two visits in a row re-orders the suggestions rather
 * than offering the stale list.
 */
export function useRecentVolunteerSessions(volunteerId: string | undefined) {
  return useQuery<RecentSession[]>({
    queryKey: sessionKeys.recentForVolunteer(volunteerId ?? ''),
    queryFn: () => listRecentVolunteerSessions(volunteerId as string),
    enabled: Boolean(volunteerId),
  });
}

/**
 * Add a session to an activity (D19).
 *
 * Invalidates every session query rather than the activity's list alone: the
 * panel that lists them and the assignment picker both read sessions, and a
 * new one must appear in both at once.
 */
export function useCreateSession(activityId: string) {
  const queryClient = useQueryClient();

  return useMutation<CreatedSession, Error, CreateSessionBody>({
    mutationFn: (body) => createSession(activityId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
    },
  });
}
